import { useEffect, useState } from "react";
import { trimTrailingSlash } from "../../lib/url";

export type SessionListItem = { session_id: string; session_date: string };

// Lean session list for the Toolkit's pickers: newest first.
// Same endpoint the app's SessionSelector uses.
export function useSessionList(
  apiBaseUrl: string,
  getToken: () => Promise<string | null>,
  patientId: string
) {
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId || !apiBaseUrl) {
      setSessions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated");
        const url = new URL(`${trimTrailingSlash(apiBaseUrl)}/sessions/list`);
        url.searchParams.set("patient_id", patientId);
        url.searchParams.set("limit", "50");
        const resp = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error((await resp.text().catch(() => "")) || `HTTP ${resp.status}`);
        const data = await resp.json();
        const list: SessionListItem[] = Array.isArray(data?.items) ? data.items : [];
        const sorted = [...list].sort((a, b) => (b.session_date || "").localeCompare(a.session_date || ""));
        if (!cancelled) setSessions(sorted);
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, patientId]);

  return { sessions, loading, error };
}
